import { Component, Prop } from 'vue-property-decorator'
import { GForm } from '@/components/forms/GForm'
import { parseToNumber, removeAccents } from '@/utils/general'
import dayjs from 'dayjs'
import _ from 'lodash'
import { ComponentValue, Year } from '@/entities/vehicle'
import { PersonType } from '@/entities/persons'
import { AcquisitionType } from '@/entities/settings'
import axios from 'axios'

const { VUE_APP_BASE_URL } = process.env
@Component
export class ImportStockView extends GForm {
    years: Year[] = []
    personTypes: PersonType[] = []
    acquisitionType: AcquisitionType[] = []
    fuels: ComponentValue[] = []
    transmissions: ComponentValue[] = []
    traction: ComponentValue[] = []
    maxVehicleAgeAllowed = null
    idProcessNegotiation = null
    keys = []
    keysAvailable = []

    async mounted () {
      const process = await this.fetchData({
        query: { name: 'find', model: 'Process' },
        filter: { table_name: { _eq: 'auto' } },
      })

      this.maxVehicleAgeAllowed = process?.[0]?.config?.maxVehicleAgeAllowed
      this.years = await this.fetchData({
        query: { name: 'find', model: 'Year' },
      })

      this.personTypes = await this.fetchData({
        query: { name: 'find', model: 'PersonType' },
      })

      this.acquisitionType = await this.fetchData({
        query: { name: 'find', model: 'AcquisitionType' },
        filter: { active: { _eq: true } },
      })

      this.fuels = await this.fetchData({
        query: { name: 'find', model: 'ComponentValue' },
        filter: {
          component: {
            name: { _eq: 'Combustible' },
          },
        },
      })

      this.traction = await this.fetchData({
        query: { name: 'find', model: 'ComponentValue' },
        filter: {
          _and: [
            { component: { name: { _eq: 'Tracción' } } },
            { value: { _neq: '4x2 y 4x4' } },
          ],
        },
      })

      this.transmissions = await this.fetchData({
        query: { name: 'find', model: 'ComponentValue' },
        filter: {
          component: {
            name: { _eq: 'Transmisión' },
          },
        },
      })

      const processNegotiation = await this.fetchData({
        query: { name: 'find', model: 'Process' },
        filter: { table_name: { _eq: 'negotiation' } },
      })

      this.idProcessNegotiation = processNegotiation[0].id

      await this.fillKeys()
    }

    async fillKeys () {
      const stocks = await this.fetchData({
        query: { name: 'findLite', model: 'Stock' },
        filter: {
          id_key_identifier: { _is_null: false },
        },
        force: true,
      })

      // Mapear las llaves ocupadas a un objeto { id, name }
      this.keys = stocks.map(stock => stock.keyIdentifier.keyId)

      const processStock = await this.fetchData({
        query: { name: 'find', model: 'Process' },
        filter: { table_name: { _eq: 'stock' } },
        force: true,
      })

      const yardCapacity = processStock[0].config.resource.capacity

      // Generar llaves disponibles como objetos { id, name }
      const keysAvailable = Array.from({ length: yardCapacity }, (_, i) => i + 1)
        .map(key => {
          // Buscar si la llave está ocupada
          const stockBusy = stocks.find(stock => stock.keyIdentifier.keyId === key)
          if (stockBusy) {
            // Si está ocupada, mostrar el nombre del auto si existe
            return {
              id: key,
              name: `${key} - ${stockBusy.auto?.name}`,
            }
          }
          // Si está disponible
          return {
            id: key,
            name: `${key} - Disponible`,
          }
        })

      this.keysAvailable = keysAvailable
    }

    getGenerateRandomKey () {
      const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
      let result = ''
      for (let i = 0; i < 4; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length))
      }
      return result
    }

    async findBrand (year, name) {
      if (!year || !name?.toString()?.length) {
        return {
          match: '',
          data: [],
        }
      }
      const brands = await this.fetchData({
        query: { name: 'findLite', model: 'Brand', order: { name: 'asc' } },
        filter: {
          models: {
            versions: {
              years: {
                id_year: {
                  _gte: parseToNumber(year) - parseToNumber(this.maxVehicleAgeAllowed),
                  _lte: parseToNumber(year) + parseToNumber(this.maxVehicleAgeAllowed),
                },
              },
            },
          },
        },
        force: true,
      })

      const payload = {
        name,
        data: brands,
      }

      try {
        const { data: { match = '' } } = await axios.post(`${VUE_APP_BASE_URL}/convert-pdf-to-image/find-match`, payload, {
          headers: {
            'Content-Type': 'application/json',
          },
        })

        // Si match es un ID, encontrar el objeto completo
        let matchedBrand = match
        if (match && (typeof match === 'string' || typeof match === 'number')) {
          // Buscar el objeto completo de la marca
          const foundBrand = brands.find(brand => brand.id === match || brand.id.toString() === match.toString())
          if (foundBrand) {
            matchedBrand = foundBrand
          }
        }

        return { match: matchedBrand, data: brands, firstLoad: true }
      } catch (error) {
        return { match: '', data: brands, firstLoad: true }
      }
    }

    async findModel (year, name, brand) {
      if (!brand?.id || !name?.toString()?.length) {
        return {
          match: '',
          data: [],
        }
      }
      const models = await this.fetchData({
        query: {
          name: 'findLite',
          model: 'Model',
          order: {
            name: 'asc',
          },
        },
        filter: {
          _and: [
            { id_brand: { _eq: brand.id } },
            {
              versions: {
                years: {
                  id_year: {
                    _gte: parseToNumber(year) - parseToNumber(this.maxVehicleAgeAllowed),
                    _lte: parseToNumber(year) + parseToNumber(this.maxVehicleAgeAllowed),
                  },
                },
              },
            },
          ],
        },
        force: true,
      })

      const payload = {
        name,
        data: models,
      }
      try {
        const { data: { match = '' } } = await axios.post(`${VUE_APP_BASE_URL}/convert-pdf-to-image/find-match`, payload, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        return { match: match ?? '', data: models, firstLoad: true }
      } catch (error) {
        console.error(error)
      }
    }

    async findVersion (year, name, model, brand) {
      if (!model?.id || !brand?.id || !name?.toString()?.length) {
        return {
          match: '',
          data: [],
        }
      }
      const versions = await this.fetchData({
        query: {
          name: 'findLite',
          model: 'VersionYear',
          order: {
            id_year: 'desc',
          },
        },
        filter: {
          _and: [
            {
              version: {
                _and: [
                  {
                    model: { _and: [{ id_brand: { _eq: brand.id } }, { id: { _eq: model?.id } }] },
                  },
                ],
              },
            },
            {
              id_year: {
                _gte: parseToNumber(year) - parseToNumber(this.maxVehicleAgeAllowed),
                _lte: parseToNumber(year) + parseToNumber(this.maxVehicleAgeAllowed),
              },
            },
          ],
        },
        force: true,
      })

      // Separamos versiones que coinciden exactamente con el año y las que no
      const exactYearVersions = versions.filter(v => v.year.id === parseToNumber(year))
      const otherYearVersions = versions.filter(v => v.year.id !== parseToNumber(year))

      // Eliminamos versiones con nombres duplicados, priorizando las del año exacto
      const uniqueVersions = []
      const versionNames = new Set()

      // Función para agregar versiones únicas a la lista
      const addUniqueVersion = v => {
        const versionName = v.version.name
        if (!versionNames.has(versionName)) {
          versionNames.add(versionName)
          uniqueVersions.push(v)
        }
      }

      // Primero procesamos las versiones del año exacto
      exactYearVersions.forEach(addUniqueVersion)

      // Luego procesamos las versiones de otros años que no estén ya incluidas
      otherYearVersions.forEach(addUniqueVersion)

      const payload = {
        name,
        data: uniqueVersions.map(v => {
          return {
            name: v.version.name,
            id: v.id,
            year: v.year.id, // Agregamos el año para referencia
          }
        }),
      }

      try {
        const { data: { match = '' } } = await axios.post(`${VUE_APP_BASE_URL}/convert-pdf-to-image/find-match`, payload, {
          headers: {
            'Content-Type': 'application/json',
          },
        })

        return { match, data: uniqueVersions, firstLoad: true }
      } catch (error) {
        console.error(error)
      }
    }

    async checkVersionYearExists (item) {
      if (!item.version?.match?.id || !item.year) return false

      const versionYear = (await this.fetchData({
        query: { name: 'findLite', model: 'VersionYear' },
        filter: {
          id: { _eq: item.version.match.id },
        },
        force: true,
      }))[0]

      // Verificar si ya existe la versión para el año seleccionado
      const existingVersions = await this.fetchData({
        query: { name: 'find', model: 'VersionYear' },
        filter: {
          _and: [
            { id_version: { _eq: versionYear.version.id } },
            { id_year: { _eq: item.year } },
          ],
        },
        force: true,
      })

      return Boolean(existingVersions.length)
    }

    async extendVersionToSelectedYear (item) {
      // Obtener la información completa de la versión seleccionada
      const versionData = await this.fetchData({
        query: { name: 'fetch', model: 'VersionYear', params: { id: item.version.match.id } },
        force: true,
      })

      if (!versionData) {
        throw new Error('No se pudo obtener la información de la versión')
      }

      // Obtener los atributos de la versión existente
      const { version, attributes, generations } = versionData

      // Determinar qué atributos usar
      let customAttributes = attributes

      // Si hay generaciones, usar los atributos de la última generación
      if (generations && generations.length > 0) {
        const generation = generations[generations.length - 1]
        const generationFetched = await this.fetchData({
          query: { name: 'fetch', model: 'Generation', params: { id: generation.id } },
        })

        if (generationFetched?.attributes) {
          customAttributes = generationFetched.attributes
        }
      }

      // Procesar los atributos para asegurar el formato correcto
      const processedAttributes = this.buildAttributes(customAttributes)

      // Crear la nueva entrada VersionYear
      const fields = {
        id_version: version.id,
        id_year: item.year,
        attributes: {
          data: processedAttributes,
        },
      }

      const newVersionYear = await this.pushData({
        model: 'VersionYear',
        fields,
      })

      // Actualizar la referencia en el item
      if (newVersionYear) {
        // Asignar el nuevo VersionYear
        item.version.match = newVersionYear

        // Actualizar idVersionYear para que coincida con el nuevo ID creado
        item.idVersionYear = newVersionYear.id

        // Importante: Limpiar idGeneration ya que la nueva versión extendida no tiene generaciones aún
        item.idGeneration = null

        // Forzar la actualización de atributos para esta nueva versión
        item.checkAttributes = true
      }

      return newVersionYear
    }

    // Procesa los atributos para asegurar el formato correcto (igual que en AutoForm.vue)
    buildAttributes (attributes: Record<string, any>[]) {
      if (!Array.isArray(attributes)) {
        throw new Error('Invalid attributes: Expected an array.')
      }

      return attributes.map(attr => {
        const hasValue = attr?.value !== undefined && attr?.value !== null
        const attributeData = {
          id_component: attr?.component?.id,
          validated: false, // Valor por defecto, se puede ajustar
          value: null,
          id_component_value: null,
        }

        if (hasValue) {
          // Si existe el valor, incluirlo
          attributeData.value = attr.value
        } else if (attr.componentValue?.id) {
          // Incluir el ID de componentValue si está disponible
          attributeData.id_component_value = attr.componentValue.id
        }

        return attributeData
      })
    }

    // Método para construir la estructura de datos para enviar al servidor
    buildRecordData (items: any[]) {
      return {
        records: items.map(item => {
        // Determinar idVersion
          const idVersion = item.idVersionYear || (item.version?.match?.id || null)
          // Determinar idGeneration solo si corresponde
          let idGeneration: number | null = null
          if (item.idGeneration && item.version?.match?.id === item.idVersionYear) {
            idGeneration = item.idGeneration
          }
          return {
            ppu: item.ppu,
            listPrice: item.priceList,
            buyPrice: item.purchasePrice,
            renewalBonus: item.renewalBonus,
            discount: item.discount,
            mileage: item.mileage,
            ownersNumber: item.owners,
            key: item.key,
            ownersInfo: item.owner.map((owner: string, index: number) => ({
              names: owner,
              uid: item.uid[index] || '',
            })),
            acquisitionType: item.acquisitionType?.description?.toLowerCase() === 'consignación'
              ? 'consignment'
              : 'purchase',
            cav: item.cav?.[0]?.id || null,
            idGeneration,
            idVersion,
            purchaseDate: item.date,
            lastOwnershipDate: item.cavSerialization.cavInfo.currOwners[0].date,
          }
        }),
      }
    }

    redirectToImport (enabling = false) {
      const stockRoute = `/datatable/supervisor/supervisor_stock?filter=${enabling ? 'enabling' : 'all'}&page=1&size=20&period=last_30_days`
      window.open(this.$router.resolve({ path: stockRoute }).href, '_blank')
    }
}
